import React from 'react';
import Joyride from 'react-joyride';
import { Badge } from '../ui/badge';

const ProductTour = ({ onFinish, onComplete, onSkip }) => {
  const steps = [
    {
      target: '[data-tour="upload-graphic"]',
      content: (
        <div className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Steg 1 / 5</Badge>
          </div>
          <h3 className="text-lg font-semibold text-foreground">Ladda upp grafik</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Här kan du ladda upp dina egna logotyper och dekaler. Dra och släpp dina filer direkt här eller klicka för att välja från din dator.
          </p>
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              💡 <strong>Tips:</strong> Vi stödjer SVG, PNG och JPG-filer. SVG-filer ger bäst kvalitet!
            </p>
          </div>
        </div>
      ),
      placement: 'left',
      disableBeacon: true
    },
    {
      target: '.text-joyride',
      content: (
        <div className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Steg 2 / 5</Badge>
          </div>
          <h3 className="text-lg font-semibold text-foreground">Lägg till text</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Skapa dekaler med hjälp av text. Du kan välja mellan olika typsnitt, färger och justeringar för att skapa den perfekta designen.
          </p>
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              💡 <strong>Tips:</strong> Klicka och dra för att flytta texten, använd handtagen för att ändra storlek och rotation.
            </p>
          </div>
        </div>
      ),
      placement: 'left',
      disableBeacon: true
    },
    {
      target: '.tabs-car-views',
      content: (
        <div className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Steg 3 / 5</Badge>
          </div>
          <h3 className="text-lg font-semibold text-foreground">Byt vy</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Se din design från alla vinklar! Växla mellan olika vyer för att placera dekaler på bilens alla sidor.
          </p>
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              💡 <strong>Tips:</strong> Du kan använda samma dekal på flera sidor av bilen - perfekt för symmetrisk design!
            </p>
          </div>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '.color-picker-section',
      content: (
        <div className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Steg 4 / 5</Badge>
          </div>
          <h3 className="text-lg font-semibold text-foreground">Välj bilfärg</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Anpassa bilens färg för att matcha din design. Välj mellan våra fördefinierade färger eller använd färgväljaren för att hitta den perfekta nyansen.
          </p>
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              💡 <strong>Tips:</strong> Testa olika färger för att se hur dina dekaler framhävs bäst!
            </p>
          </div>
        </div>
      ),
      placement: 'left',
    },
    {
      target: '.sidebar-summary',
      content: (
        <div className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Steg 5 / 5</Badge>
          </div>
          <h3 className="text-lg font-semibold text-foreground">Pris & sammanfattning</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Här ser du totalpriset för din design. Priset uppdateras automatiskt baserat på antal och storlek på dina dekaler.
          </p>
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              💡 <strong>Tips:</strong> Klicka på "Prisuträkning" för att se en detaljerad översikt av kostnaderna.
            </p>
          </div>
        </div>
      ),
      placement: 'top',
    }
  ];

  const handleCallback = (data) => {
    const { status, type } = data;
    
    if (status === 'finished' && onFinish) {
      onFinish();
    }
    
    if (type === 'tour:end' && onComplete) {
      onComplete();
    }

    if (type === 'tour:skip' && onSkip) {
      onSkip();
    }
  };

  // Get CSS variables from the document
  const getComputedColor = (varName) => {
    if (typeof window !== 'undefined') {
      return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    }
    return '';
  };

  return (
    <Joyride
      steps={steps}
      continuous={true}
      showSkipButton={true}
      showStepsProgress={false}
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: 'hsl(var(--primary))',
          textColor: 'hsl(var(--foreground))',
          backgroundColor: 'hsl(var(--card))',
          arrowColor: 'hsl(var(--card))',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000
        },
        tooltipContainer: {
          textAlign: 'left',
          padding: '0'
        },
        tooltipContent: {
          padding: '0'
        },
        buttonNext: {
          backgroundColor: 'hsl(var(--primary))',
          color: 'hsl(var(--primary-foreground))',
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: '500',
          borderRadius: '6px',
          border: 'none',
          outline: 'none'
        },
        buttonBack: {
          color: 'hsl(var(--muted-foreground))',
          marginRight: '12px',
          padding: '8px 16px',
          fontSize: '14px'
        },
        buttonSkip: {
          color: 'hsl(var(--muted-foreground))',
          fontSize: '14px'
        },
        tooltip: {
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
          border: '1px solid hsl(var(--border))'
        }
      }}
      locale={{
        last: 'Klar',
        next: 'Nästa',
        skip: 'Hoppa över',
        back: 'Tillbaka'
      }}
    />
  );
};

export default ProductTour; 